import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config';
import {
  CircleNotch, Funnel, Shield, Lightning, CheckCircle, XCircle,
  ArrowLeft, Eye, Package, Storefront, Check, X, Warning,
  CaretDown, Clock, Power, ChartBar, TrendUp, TrendDown, Fire, Download
} from '@phosphor-icons/react';

const STATUS_CONFIG = {
  draft: { label: 'Draft', className: 'bg-gray-500/10 text-gray-400' },
  submitted: { label: 'Submitted', className: 'bg-amber-500/10 text-amber-400' },
  in_review: { label: 'In Review', className: 'bg-amber-500/10 text-amber-400' },
  approved: { label: 'Approved', className: 'bg-blue-500/10 text-blue-400' },
  published: { label: 'Published', className: 'bg-emerald-500/10 text-emerald-400' },
  rejected: { label: 'Rejected', className: 'bg-red-500/10 text-red-400' },
  suspended: { label: 'Suspended', className: 'bg-orange-500/10 text-orange-400' },
};

const TRUST_CLASSES = ['native_managed', 'verified_partner', 'workspace_private', 'external_registered'];

// ─── Analytics Panel ──────────────────────────────────────────
const AnalyticsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [window, setWindow] = useState(30);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${BACKEND_URL}/api/app-store/analytics/admin?time_window_days=${window}`, { credentials: 'include' });
        if (res.ok) setData(await res.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchAnalytics();
  }, [window]);

  if (loading) return <div className="flex justify-center py-16"><CircleNotch size={28} className="animate-spin text-[var(--brand-primary)]" /></div>;
  if (!data) return <p className="text-center py-8 text-[var(--text-muted)] text-sm">Unable to load analytics</p>;

  const pt = data.platform_totals || {};

  return (
    <div className="space-y-6" data-testid="admin-analytics-panel">
      {/* Time window selector */}
      <div className="flex items-center gap-2">
        <Clock size={14} className="text-[var(--text-muted)]" />
        <span className="text-xs text-[var(--text-muted)]">Time window:</span>
        {[7, 30, 90].map(d => (
          <button
            key={d}
            onClick={() => setWindow(d)}
            className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${window === d ? 'bg-[var(--brand-primary)] text-white' : 'bg-[var(--surface-2)] text-[var(--text-muted)] hover:text-[var(--text-primary)]'}`}
            data-testid={`window-${d}d`}
          >
            {d}d
          </button>
        ))}
      </div>

      {/* Platform Totals */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total Invocations', value: pt.total_invocations || 0, color: 'text-[var(--brand-primary)]' },
          { label: 'Success Rate', value: pt.success_rate != null ? `${pt.success_rate}%` : 'N/A', color: pt.success_rate >= 90 ? 'text-emerald-400' : pt.success_rate >= 70 ? 'text-amber-400' : 'text-red-400' },
          { label: 'Active Installs', value: pt.total_active_installations || 0, color: 'text-blue-400' },
          { label: 'Apps Invoked', value: pt.unique_apps_invoked || 0, color: 'text-purple-400' },
        ].map(s => (
          <div key={s.label} className="bg-[var(--surface-1)] border border-[var(--border-default)] rounded-xl p-4 text-center">
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-[10px] text-[var(--text-muted)] mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Most Used */}
        <div className="bg-[var(--surface-1)] border border-[var(--border-default)] rounded-xl p-4">
          <h3 className="text-xs font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-1.5"><TrendUp size={14} weight="bold" className="text-emerald-400" />Most Used Apps</h3>
          {(data.most_used || []).length === 0 ? (
            <p className="text-xs text-[var(--text-muted)] py-4 text-center">No invocation data yet</p>
          ) : (
            <div className="space-y-2">
              {data.most_used.map((app, i) => (
                <div key={app.app_id} className="flex items-center justify-between p-2 bg-[var(--surface-2)] rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-[var(--text-muted)] w-4">{i + 1}.</span>
                    <span className="text-xs text-[var(--text-primary)] font-medium truncate max-w-[160px]">{app.app_name || app.app_id}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-[var(--text-secondary)]">{app.invocations} inv.</span>
                    <span className={`text-[10px] ${app.failures > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                      {app.successes}ok / {app.failures}fail
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Most Installed */}
        <div className="bg-[var(--surface-1)] border border-[var(--border-default)] rounded-xl p-4">
          <h3 className="text-xs font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-1.5"><Download size={14} weight="bold" className="text-blue-400" />Most Installed Apps</h3>
          {(data.most_installed || []).length === 0 ? (
            <p className="text-xs text-[var(--text-muted)] py-4 text-center">No installations yet</p>
          ) : (
            <div className="space-y-2">
              {data.most_installed.map((app, i) => (
                <div key={app.app_id} className="flex items-center justify-between p-2 bg-[var(--surface-2)] rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-[var(--text-muted)] w-4">{i + 1}.</span>
                    <span className="text-xs text-[var(--text-primary)] font-medium truncate max-w-[160px]">{app.app_name || app.app_id}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-[var(--text-secondary)]">{app.installs} installs</span>
                    <span className="text-[10px] text-[var(--text-muted)]">{app.workspace_count} ws</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Error Prone */}
        <div className="bg-[var(--surface-1)] border border-[var(--border-default)] rounded-xl p-4">
          <h3 className="text-xs font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-1.5"><Warning size={14} weight="bold" className="text-red-400" />Error-Prone Apps</h3>
          {(data.error_prone || []).length === 0 ? (
            <p className="text-xs text-[var(--text-muted)] py-4 text-center">No error-prone apps</p>
          ) : (
            <div className="space-y-2">
              {data.error_prone.map(app => (
                <div key={app.app_id} className="flex items-center justify-between p-2 bg-[var(--surface-2)] rounded-lg">
                  <span className="text-xs text-[var(--text-primary)] font-medium truncate max-w-[180px]">{app.app_name || app.app_id}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-red-400">{app.failure_rate}% fail</span>
                    <span className="text-[10px] text-[var(--text-muted)]">{app.failures}/{app.total}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top Capabilities */}
        <div className="bg-[var(--surface-1)] border border-[var(--border-default)] rounded-xl p-4">
          <h3 className="text-xs font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-1.5"><Fire size={14} weight="bold" className="text-amber-400" />Top Capabilities</h3>
          {(data.top_capabilities || []).length === 0 ? (
            <p className="text-xs text-[var(--text-muted)] py-4 text-center">No capability data yet</p>
          ) : (
            <div className="space-y-2">
              {data.top_capabilities.map(cap => (
                <div key={cap.capability_id} className="flex items-center justify-between p-2 bg-[var(--surface-2)] rounded-lg">
                  <span className="text-xs text-[var(--text-primary)] font-medium">{(cap.capability_id || '').replace(/_/g, ' ')}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[var(--text-secondary)]">{cap.invocations} inv.</span>
                    <span className="text-[10px] text-[var(--text-muted)]">{cap.app_count} app{cap.app_count !== 1 ? 's' : ''}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AdminAppReviewQueue = () => {
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('submitted');
  const [selected, setSelected] = useState(null);
  const [actionLoading, setActionLoading] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [trustClass, setTrustClass] = useState('verified_partner');
  const [activeTab, setActiveTab] = useState('queue');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const q = filter ? `?status=${filter}` : '';
      const [queueRes, statsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/app-store/admin/queue${q}`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/app-store/admin/queue/stats`, { credentials: 'include' }),
      ]);
      if (queueRes.ok) {
        const data = await queueRes.json();
        setSubmissions(data.submissions || []);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data.by_status || {});
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAction = async (id, action) => {
    setActionLoading(action);
    try {
      const body = {};
      if (action === 'approve') {
        body.notes = reviewNotes;
        body.trust_class = trustClass;
      } else if (action === 'reject' || action === 'suspend') {
        body.notes = reviewNotes;
      }
      const res = await fetch(`${BACKEND_URL}/api/app-store/admin/review/${id}/${action}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setSelected(null);
        setReviewNotes('');
        fetchData();
      }
    } catch (e) { console.error(e); }
    finally { setActionLoading(''); }
  };

  const pendingCount = (stats.submitted || 0) + (stats.in_review || 0);

  return (
    <div className="min-h-screen bg-[var(--surface-0)] p-6" data-testid="admin-app-review-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[var(--text-primary)] flex items-center gap-2">
            <Storefront size={24} weight="duotone" className="text-[var(--brand-primary)]" />
            App Review Queue
          </h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">Review and manage partner app submissions.</p>
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center gap-1 bg-[var(--surface-1)] border border-[var(--border-default)] rounded-lg p-0.5 mb-6">
          {[['queue', 'Review Queue', Storefront], ['analytics', 'Usage Analytics', ChartBar]].map(([val, label, Icon]) => (
            <button
              key={val}
              onClick={() => setActiveTab(val)}
              className={`px-4 py-2 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5 ${activeTab === val ? 'bg-[var(--brand-primary)] text-white' : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'}`}
              data-testid={`tab-${val}`}
            >
              <Icon size={14} />{label}
            </button>
          ))}
        </div>

        {activeTab === 'analytics' ? (
          <AnalyticsPanel />
        ) : (
          <>
        {/* Stats */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Pending', count: pendingCount, color: 'text-amber-400' },
            { label: 'Approved', count: stats.approved || 0, color: 'text-blue-400' },
            { label: 'Published', count: stats.published || 0, color: 'text-emerald-400' },
            { label: 'Rejected', count: stats.rejected || 0, color: 'text-red-400' },
          ].map(s => (
            <div key={s.label} className="bg-[var(--surface-1)] border border-[var(--border-default)] rounded-xl p-4 text-center">
              <p className={`text-xl font-bold ${s.color}`}>{s.count}</p>
              <p className="text-[10px] text-[var(--text-muted)] mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Filter */}
        <div className="flex items-center gap-3 mb-4">
          <Funnel size={16} className="text-[var(--text-muted)]" />
          <select
            value={filter}
            onChange={e => setFilter(e.target.value)}
            className="px-3 py-1.5 bg-[var(--surface-2)] border border-[var(--border-default)] rounded-lg text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--brand-primary)]"
            data-testid="admin-status-filter"
          >
            <option value="">All</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>{v.label}{stats[k] ? ` (${stats[k]})` : ''}</option>
            ))}
          </select>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-16"><CircleNotch size={28} className="animate-spin text-[var(--brand-primary)]" /></div>
        ) : submissions.length === 0 ? (
          <div className="text-center py-16 bg-[var(--surface-1)] rounded-xl border border-[var(--border-default)]">
            <CheckCircle size={40} className="mx-auto text-emerald-400 mb-3" />
            <p className="text-[var(--text-secondary)] text-sm">No submissions in this queue</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-submissions-list">
            {submissions.map(sub => {
              const status = STATUS_CONFIG[sub.status] || STATUS_CONFIG.draft;
              return (
                <div
                  key={sub.submission_id}
                  onClick={() => { setSelected(sub); setReviewNotes(''); setTrustClass(sub.trust_class || 'verified_partner'); }}
                  className="bg-[var(--surface-1)] border border-[var(--border-default)] rounded-xl p-4 cursor-pointer hover:border-[var(--brand-primary)]/30 transition-all"
                  data-testid={`admin-sub-${sub.submission_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-[var(--text-primary)]">{sub.name}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${status.className}`}>{status.label}</span>
                      </div>
                      <p className="text-xs text-[var(--text-secondary)] mt-1 line-clamp-1">{sub.description}</p>
                      <div className="flex items-center gap-3 mt-2 text-[10px] text-[var(--text-muted)]">
                        <span className="flex items-center gap-1"><Lightning size={10} />{sub.capabilities?.length || 0} capabilities</span>
                        <span>{sub.auth_model?.replace(/_/g, ' ')}</span>
                        <span>by {sub.developer_name || sub.creator_name || 'Unknown'}</span>
                        {sub.submitted_at && <span>{new Date(sub.submitted_at).toLocaleDateString()}</span>}
                      </div>
                    </div>
                    <Eye size={16} className="text-[var(--text-muted)]" />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Review Modal */}
        {selected && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
            <div className="bg-[var(--surface-1)] border border-[var(--border-default)] rounded-2xl w-full max-w-xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="admin-review-modal">
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-[var(--text-primary)]">Review: {selected.name}</h2>
                  <button onClick={() => setSelected(null)} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"><X size={18} /></button>
                </div>

                <p className="text-sm text-[var(--text-secondary)]">{selected.description}</p>

                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-[var(--surface-2)] rounded-lg">
                    <p className="text-[10px] text-[var(--text-muted)] uppercase mb-1">Auth Model</p>
                    <p className="text-xs text-[var(--text-primary)]">{selected.auth_model?.replace(/_/g, ' ')}</p>
                  </div>
                  <div className="p-3 bg-[var(--surface-2)] rounded-lg">
                    <p className="text-[10px] text-[var(--text-muted)] uppercase mb-1">Developer</p>
                    <p className="text-xs text-[var(--text-primary)]">{selected.developer_name || selected.creator_name}</p>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold text-[var(--text-primary)] mb-2">Capabilities</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(selected.capabilities || []).map(cap => (
                      <span key={cap} className="px-2 py-0.5 bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] rounded text-xs">{cap.replace(/_/g, ' ')}</span>
                    ))}
                  </div>
                </div>

                {selected.provider_config && Object.keys(selected.provider_config).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-[var(--text-primary)] mb-2">Provider Config</p>
                    <pre className="p-3 bg-[var(--surface-2)] rounded-lg text-[10px] text-[var(--text-secondary)] overflow-x-auto font-mono">
                      {JSON.stringify(selected.provider_config, null, 2)}
                    </pre>
                  </div>
                )}

                {/* Admin Actions */}
                {['submitted', 'in_review'].includes(selected.status) && (
                  <div className="border-t border-[var(--border-default)] pt-4 space-y-3">
                    <div>
                      <label className="text-xs text-[var(--text-muted)] mb-1 block">Trust Class (for approval)</label>
                      <select
                        value={trustClass}
                        onChange={e => setTrustClass(e.target.value)}
                        className="w-full px-3 py-2 bg-[var(--surface-3)] border border-[var(--border-default)] rounded-lg text-sm text-[var(--text-primary)]"
                        data-testid="trust-class-select"
                      >
                        {TRUST_CLASSES.map(tc => <option key={tc} value={tc}>{tc.replace(/_/g, ' ')}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-[var(--text-muted)] mb-1 block">Review Notes</label>
                      <textarea
                        rows={2}
                        value={reviewNotes}
                        onChange={e => setReviewNotes(e.target.value)}
                        placeholder="Add notes for the developer..."
                        className="w-full px-3 py-2 bg-[var(--surface-3)] border border-[var(--border-default)] rounded-lg text-sm text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none resize-none"
                        data-testid="review-notes-textarea"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleAction(selected.submission_id, 'approve')}
                        disabled={!!actionLoading}
                        className="flex-1 px-4 py-2 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-lg text-sm font-medium hover:bg-emerald-500/20 transition-colors flex items-center justify-center gap-1.5"
                        data-testid="approve-app-btn"
                      >
                        {actionLoading === 'approve' ? <CircleNotch size={14} className="animate-spin" /> : <CheckCircle size={16} />}Approve
                      </button>
                      <button
                        onClick={() => handleAction(selected.submission_id, 'reject')}
                        disabled={!!actionLoading}
                        className="flex-1 px-4 py-2 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg text-sm font-medium hover:bg-red-500/20 transition-colors flex items-center justify-center gap-1.5"
                        data-testid="reject-app-btn"
                      >
                        {actionLoading === 'reject' ? <CircleNotch size={14} className="animate-spin" /> : <XCircle size={16} />}Reject
                      </button>
                    </div>
                  </div>
                )}

                {selected.status === 'approved' && (
                  <div className="border-t border-[var(--border-default)] pt-4">
                    <button
                      onClick={() => handleAction(selected.submission_id, 'publish')}
                      disabled={!!actionLoading}
                      className="w-full px-4 py-2 bg-[var(--brand-primary)] text-white rounded-lg text-sm font-medium hover:bg-[var(--brand-primary-hover)] transition-colors flex items-center justify-center gap-1.5"
                      data-testid="publish-app-btn"
                    >
                      {actionLoading === 'publish' ? <CircleNotch size={14} className="animate-spin" /> : <Storefront size={16} />}Publish to App Store
                    </button>
                  </div>
                )}

                {selected.status === 'published' && (
                  <div className="border-t border-[var(--border-default)] pt-4">
                    <div className="mb-3">
                      <label className="text-xs text-[var(--text-muted)] mb-1 block">Suspension Reason</label>
                      <textarea
                        rows={2}
                        value={reviewNotes}
                        onChange={e => setReviewNotes(e.target.value)}
                        placeholder="Reason for suspension..."
                        className="w-full px-3 py-2 bg-[var(--surface-3)] border border-[var(--border-default)] rounded-lg text-sm text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none resize-none"
                      />
                    </div>
                    <button
                      onClick={() => handleAction(selected.submission_id, 'suspend')}
                      disabled={!!actionLoading}
                      className="w-full px-4 py-2 bg-orange-500/10 text-orange-400 border border-orange-500/20 rounded-lg text-sm font-medium hover:bg-orange-500/20 transition-colors flex items-center justify-center gap-1.5"
                      data-testid="suspend-app-btn"
                    >
                      {actionLoading === 'suspend' ? <CircleNotch size={14} className="animate-spin" /> : <Power size={16} />}Suspend App
                    </button>
                  </div>
                )}

                {selected.status === 'suspended' && (
                  <div className="border-t border-[var(--border-default)] pt-4">
                    <button
                      onClick={() => handleAction(selected.submission_id, 'republish')}
                      disabled={!!actionLoading}
                      className="w-full px-4 py-2 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-lg text-sm font-medium hover:bg-emerald-500/20 transition-colors flex items-center justify-center gap-1.5"
                      data-testid="republish-app-btn"
                    >
                      {actionLoading === 'republish' ? <CircleNotch size={14} className="animate-spin" /> : <Storefront size={16} />}Republish
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminAppReviewQueue;
